import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, Snackbar, Alert, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import { useForm } from 'react-hook-form';
import { ThemeInputText, ThemeTimePicker, FormCard, GrayColorButton, SecondColorButton } from './Controls';
import { apiURL } from './App';
import { authJson, authFetch } from './apiClient';

// 型定義
interface DataItem { id: string; name: string; length?: string; }
interface CreateForm { name: string; length?: string; }
interface EditForm { name: string; length?: string; }

// DataGrid カスタムスタイル（再マウント防止のためコンポーネント外で定義）
const ThemeDataGrid = styled(DataGrid)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.primary.main,
    outline: 'none',
    boxShadow: 'none',
  },
  '& .MuiDataGrid-menuIconButton, & .MuiDataGrid-menuIcon, & .MuiDataGrid-iconButtonContainer': {
    display: 'none !important',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
    boxShadow: 'none',
  },
  '& .MuiDataGrid-filler': {
    backgroundColor: theme.palette.secondary.light,
  },
  '& .MuiDataGrid-root': {
    backgroundColor: theme.palette.secondary.light,
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
}));

// 管理者用クリップ管理パネル
const AdminClipsPanel: React.FC = () => {
  // 一覧データ
  const [rows, setRows] = useState<DataItem[]>([]);
  // フィルタ
  const { control: searchControl, watch: watchSearch, setValue: setSearchValue } = useForm<{ keyword: string }>({ defaultValues: { keyword: '' } });
  const keyword = watchSearch('keyword');
  // 追加フォーム
  const { control: addControl, handleSubmit: handleAddSubmit, reset: resetAdd, getValues: getAddValues } = useForm<CreateForm>({ defaultValues: { name: '', length: '' } });
  // 編集フォーム
  const { control: editControl, handleSubmit: handleEditSubmit, reset: resetEdit, setValue: setEditValue } = useForm<EditForm>({ defaultValues: { name: '', length: '' } });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 通知
  const [toast, setToast] = useState<{ open: boolean; msg: string; severity: 'success'|'error'|'info'|'warning' }>({ open: false, msg: '', severity: 'info' });
  const openToast = (msg: string, severity: typeof toast.severity = 'info') => setToast({ open: true, msg, severity });

  // ビューポート高さ調整用
  const manageCardRef = useRef<HTMLDivElement | null>(null);
  const [panelHeight, setPanelHeight] = useState<number | null>(null);
  useEffect(() => {
    const calc = () => {
      if (!manageCardRef.current) return;
      const rect = manageCardRef.current.getBoundingClientRect();
      const marginBottom = 16; // 下余白
      const h = window.innerHeight - rect.top - marginBottom;
      setPanelHeight(h > 300 ? h : 300); // 最低高さ 300
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  // スクロール位置復元用
  const gridScrollRef = useRef<HTMLDivElement | null>(null);
  const lastScrollTopRef = useRef<number>(0);

  // 一覧取得
  const fetchRows = useCallback(async () => {
    try {
      // API 呼び出し
      const data: any[] = await authJson(`${apiURL}/master/clips`);
      // リスト設定
      setRows((data || []).map(d => ({ id: String(d.id), name: d.name, length: d.length })));
      // 選択行が消えていない場合はそのまま (DataGrid 側に rowSelectionModel 渡しているのでOK)
      // スクロール位置復元
      requestAnimationFrame(() => {
        if (gridScrollRef.current) {
          gridScrollRef.current.scrollTop = lastScrollTopRef.current;
        }
      });
    }
    catch (e) {
      // 取得エラー
      console.error(e);
      openToast('クリップ一覧の取得に失敗しました', 'error');
      // リストクリア
      setRows([]);
    }
  }, []);

  // 初回ロード
  useEffect(() => {
    // リスト取得
    void fetchRows();
  }, [fetchRows]);

  // 編集対象選択
  const onSelectionChange = (sel: GridRowSelectionModel) => {
    // 選択行があればそのID、なければnull
    const id = sel.length ? String(sel[0]) : null;
    setSelectedId(id);
    // 編集フォームに設定
    if (id) {
      // ID検索
      const item = rows.find(p => p.id === id);
      if (item) {
        // 編集フォームに設定
        setEditValue('name', item.name);
        setEditValue('length', item.length || '');
      }
    }
    else {
      // クリア
      resetEdit();
    }
  };

  // 追加
  const onAdd = async (form: CreateForm) => {
    // 入力チェック
    if (!form.name?.trim()) { openToast('クリップ名を入力してください', 'warning'); return; }
    // 追加処理
    try {
      // API 呼び出し 
      const fd = new FormData();
      fd.append('props', JSON.stringify({ clip_name: form.name.trim(), clip_length: form.length ? form.length.trim() : null }));
      const res = await authFetch(`${apiURL}/master/clips/create`, { method: 'POST', body: fd, actionName: 'AddClip' });
      if (res.ok) {
        // 追加成功
        openToast('クリップを追加しました', 'success');
        // 追加フォームクリア
        resetAdd();
      }
      // 追加後に一覧再取得
      await fetchRows();
    }
    catch (e) {
      // 追加エラー
      console.error(e);
      openToast('追加に失敗しました', 'error');
    }
  };

  // 更新
  const onUpdate = async (form: EditForm) => {
    // 入力チェック
    if (!selectedId) { openToast('クリップを選択してください', 'warning'); return; }
    if (!form.name?.trim()) { openToast('クリップ名を入力してください', 'warning'); return; }
    // 更新処理
    try {
      // API 呼び出し 
      const fd = new FormData();
      fd.append('props', JSON.stringify({ id: selectedId, clip_name: form.name.trim(), clip_length: form.length ? form.length.trim() : null }));
      const res = await authFetch(`${apiURL}/master/clips/update`, { method: 'POST', body: fd, actionName: 'EditClip'});
      if (res.ok) {
        // 更新成功
        openToast('更新しました', 'success');
        // 一覧再取得
        await fetchRows();
      }
    }
    catch (e) {
      // 更新エラー
      console.error(e);
      openToast('更新に失敗しました', 'error');
    }
  };

  // 削除確認ダイアログ制御
  const [deleteOpen, setDeleteOpen] = useState(false);
  const onDelete = () => {
    if (!selectedId) { openToast('削除対象を選択してください', 'warning'); return; }
    setDeleteOpen(true);
  };
  // 削除ダイアログ閉じる
  const closeDeleteDialog = () => setDeleteOpen(false);

  // 削除
  const handleDeleteConfirm = async () => {
    try {
      // API 呼び出し
      const fd = new FormData();
      if (selectedId) fd.append('props', JSON.stringify({ id: selectedId }));
      const res = await authFetch(`${apiURL}/master/clips/delete`, { method: 'POST', body: fd, actionName: 'RemoveClip' });
      if (res.ok) {
        // 削除成功
        openToast('削除しました', 'success');
        // 選択解除 & 編集フォームクリア
        setSelectedId(null);
        resetEdit();
        // 一覧再取得
        await fetchRows();
      }
    }
    catch (e) {
      // 削除エラー
      console.error(e);
      openToast('削除に失敗しました', 'error');
    }
    // ダイアログ閉じる
    setDeleteOpen(false);
  };

  // フィルタ適用
  const filteredRows = rows.filter(p => !keyword?.trim() || p.name.toLowerCase().includes(keyword.trim().toLowerCase()));

  // グリッド列定義
  const columns: GridColDef[] = [
    { field: 'name', headerName: 'クリップ名', flex: 1 }
  ];

  // DataGrid スクロール位置保持: DataGrid 内部の仮想スクロール要素を取得
  const gridContainerCallback = (el: HTMLDivElement | null) => {
    if (el) {
      // DataGrid のスクロールコンテナ (MuiDataGrid-virtualScroller) を探す
      const scroller = el.querySelector('.MuiDataGrid-virtualScroller');
      if (scroller instanceof HTMLDivElement) {
        if (!gridScrollRef.current) {
          // 初期登録: スクロールイベントで位置保存
          scroller.addEventListener('scroll', () => {
            lastScrollTopRef.current = scroller.scrollTop;
          });
        }
        gridScrollRef.current = scroller;
      }
    }
  };

  // レンダリング
  return (
    <Box display="flex" gap={2}>
      {/* 左側: 固定クリップ追加 (35%) */}
      <Box sx={{ width: '35%' }}>
        <FormCard style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', alignSelf: 'flex-start', mb: 2 }}>固定クリップ追加</Typography>
          <form onSubmit={handleAddSubmit(onAdd)}>
            <Stack direction="column" spacing={2}>
              <ThemeInputText id="name" label="クリップ名" placeholder="追加するクリップ名を入力" control={addControl} />
              <ThemeTimePicker id="length" label="クリップ尺" control={addControl} showSeconds={true}/>
              <Box display="flex" gap={1} justifyContent="flex-end">
                <GrayColorButton onClick={() => resetAdd()}>クリア</GrayColorButton>
                <SecondColorButton type="submit">追加</SecondColorButton>
              </Box>
            </Stack>
          </form>
        </FormCard>
      </Box>
      {/* 右側: 固定クリップ管理 (65%) */}
      <Box sx={{ width: '65%' }}>
        <FormCard ref={manageCardRef} style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', height: panelHeight ? panelHeight : 'auto' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', alignSelf: 'flex-start', mb: 2 }}>固定クリップ管理</Typography>
          <Box sx={{ mb: 2 }}>
            <ThemeInputText id="keyword" label="クリップ検索" placeholder="クリップ名でフィルタ" control={searchControl} />
          </Box>
          <Box display="flex" gap={2} alignItems="flex-start" sx={{ flexGrow: 1, minHeight: 0 }}>
            {/* DataGrid 45% */}
            <Box sx={{ width: '45%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box sx={{ flexGrow: 1, minHeight: 0 }} ref={gridContainerCallback}>
                <ThemeDataGrid
                  rows={filteredRows}
                  columns={columns}
                  checkboxSelection={false}
                  onRowSelectionModelChange={onSelectionChange}
                  rowSelectionModel={selectedId ? [selectedId] : []}
                  hideFooter
                  isCellEditable={() => false}
                  localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                  sx={{ height: '100%' }}
                />
              </Box>
            </Box>
            {/* 編集パネル & 削除ボタン 55% */}
            <Box sx={{ width: '55%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* 編集パネル */}
              <FormCard style={{ width: '100%' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>固定クリップ名称編集</Typography>
                <form onSubmit={handleEditSubmit(onUpdate)}>
                  <Stack direction="column" spacing={2}>
                  <ThemeInputText id="name" label="クリップ名" placeholder="クリップ名を入力" control={editControl} />
                  <ThemeTimePicker id="length" label="クリップ尺" control={editControl} showSeconds={true}/>
                  <Box display="flex" gap={1} justifyContent="flex-end">
                    <GrayColorButton onClick={() => { resetEdit(); setSelectedId(null); }}>クリア</GrayColorButton>
                    <SecondColorButton type="submit">更新</SecondColorButton>
                  </Box>
                  </Stack>
                </form>
              </FormCard>
              {/* 余白用ストレッチ */}
              <Box sx={{ flexGrow: 1 }} />
              {/* 削除ボタン */}
              <Box sx={{ pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" color="error" onClick={onDelete}>クリップを削除</Button>
              </Box>
            </Box>
          </Box>
        </FormCard>
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={(e, r) => { if (r === 'clickaway') return; setToast(t => ({ ...t, open: false })); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast(t => ({ ...t, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
      <Dialog open={deleteOpen} onClose={closeDeleteDialog}>
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <Typography variant="body2">選択中のクリップを削除します。よろしいですか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} variant="contained" size="small" sx={{ color: 'white', backgroundColor: 'gray', '&:hover': { backgroundColor: '#555' } }} autoFocus>キャンセル</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" size="small">削除</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminClipsPanel;
